import { Upload } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import i18n from "i18next";
import type { FC } from "react";
import styled from "styled-components";

import { Palette } from "../../core/styles/palette";
import { SecondaryButton } from "../Buttons/components/ActionButton";
import type { ImportResult } from "./types";

type RenderImportResultProps = {
    importResult: ImportResult | null;
    showAllErrors: boolean;
    setShowAllErrors: (showAll: boolean) => void;
    successString?: string;
    errorString?: string;
    format?: string;
};

export const RenderImportResult: FC<RenderImportResultProps> = ({
    importResult,
    showAllErrors,
    setShowAllErrors,
    successString,
    errorString,
    format = "csv",
}) => {
    if (!importResult) return null;

    const hasErrors = importResult.importErrors && importResult.importErrors.length > 0;

    return (
        <ImportResultContainer hasErrors={!!hasErrors}>
            {hasErrors && (
                <ImportErrorTitle>
                    {errorString ? i18n.t(errorString) : i18n.t("csvImport.modal.errors", { type: format?.toUpperCase() })}
                </ImportErrorTitle>
            )}
            {!hasErrors && (
                <ImportSuccessTitle>
                    {successString ? i18n.t(successString) : i18n.t("csvImport.modal.successful", { type: format?.toUpperCase() })}
                </ImportSuccessTitle>
            )}
            {typeof importResult.importSuccessCount === "number" && importResult.importSuccessCount > 0 && (
                <ImportInfoText>
                    {i18n.t("csvImport.modal.recordsUpdated")} {importResult.importSuccessCount}
                </ImportInfoText>
            )}
            {typeof importResult.importFailedCount === "number" && importResult.importFailedCount > 0 && (
                <ImportErrorCountText>
                    {i18n.t("csvImport.modal.numberOfErrors")} {importResult.importFailedCount}
                </ImportErrorCountText>
            )}
            {hasErrors && (
                <>
                    <ImportErrorList>
                        {importResult.importErrors?.slice(0, showAllErrors ? undefined : 5).map((error) => (
                            <li key={error}>{i18n.t(error)}</li>
                        ))}
                    </ImportErrorList>
                    {importResult.importErrors && importResult.importErrors.length > 5 && (
                        <SecondaryButton
                            onClick={() => setShowAllErrors(!showAllErrors)}
                            style={{ marginTop: 8, fontSize: "12px", height: "28px" }}
                        >
                            {showAllErrors
                                ? i18n.t("csvImport.modal.showLess")
                                : i18n.t("csvImport.modal.showMore", {
                                      "0": String(importResult.importErrors.length - 5),
                                  })}
                        </SecondaryButton>
                    )}
                </>
            )}
        </ImportResultContainer>
    );
};

export const uploadPropsUtils = (
    fileList: UploadFile[],
    setFileList: (files: UploadFile[]) => void,
    options?: { maxSizeBytes?: number },
    setImportResult?: (result: ImportResult) => void,
) => ({
    onRemove: (file: UploadFile) => {
        const index = fileList.indexOf(file);
        const newFileList = fileList.slice();
        newFileList.splice(index, 1);
        setFileList(newFileList);
    },
    beforeUpload: (file: File) => {
        // Enforce optional max file size limit
        if (options?.maxSizeBytes && file.size > options.maxSizeBytes) {
            // Show error and ignore adding the file to the list
            const importResult2: ImportResult = {
                importFailedCount: 1,
                importErrors: ["csvImport.modal.fileTooLarge"],
            };

            if (importResult2 && setImportResult) {
                setImportResult(importResult2);
            }

            return Upload.LIST_IGNORE;
        }

        const uploadFile: UploadFile = {
            uid: String(Date.now()),
            name: file.name,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            originFileObj: file as any,
        };
        setFileList([uploadFile]);
        return false;
    },
    fileList,
    accept: ".csv, .zip, .adb",
    maxCount: 1,
});

export const ImportResultContainer = styled.div<{ hasErrors: boolean }>`
    margin-top: 16px;
    padding: 12px;
    background-color: ${(props) => (props.hasErrors ? Palette.PalePink : Palette.PaleGreen)};
    border: 1px solid ${(props) => (props.hasErrors ? Palette.LightPink : Palette.LightGreen)};
    border-radius: 4px;
`;

export const ImportErrorTitle = styled.p`
    margin: 0;
    margin-bottom: 8px;
    font-weight: bold;
    color: ${Palette.AeviRed};
`;

export const ImportSuccessTitle = styled.p`
    color: ${Palette.HeroGreen};
    margin: 0;
    margin-bottom: 8px;
    font-weight: bold;
`;

export const ImportInfoText = styled.p`
    margin: 0;
`;

export const ImportErrorCountText = styled.p`
    margin: 0;
    color: ${Palette.AeviRed};
`;

export const ImportErrorList = styled.ul`
    margin: 0;
    padding-left: 20px;
    color: ${Palette.AeviRed};
`;
